import { CalendarDays, ChevronDown } from "lucide-react";
import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "../../ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
	COMPARISON_OPTIONS,
	ComparisonMode,
	DateRangePreset,
	ReportDateRange,
} from "../../../models/ReportBuilder";
import { DATE_RANGE_PRESETS, formatRangeShort, presetLabel, resolveDateRange } from "./dateRange";

interface DateRangePickerProps {
	range: ReportDateRange;
	compare?: ComparisonMode;
	onApply: (range: ReportDateRange, compare?: ComparisonMode) => void;
}

const COMPARE_MODES = COMPARISON_OPTIONS.filter((o) => o.value !== "none") as {
	value: ComparisonMode;
	label: string;
	short: string;
}[];

/**
 * One chip shows the report's period and comparison; everything else lives in
 * a popover with presets, custom dates and the comparison, applied together.
 */
const DateRangePicker: React.FC<DateRangePickerProps> = ({ range, compare, onApply }) => {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState<{ range: ReportDateRange; compare?: ComparisonMode }>({
		range,
		compare,
	});
	const resolved = resolveDateRange(range);
	const draftResolved = resolveDateRange(draft.range);
	const compareLabel = COMPARISON_OPTIONS.find((o) => o.value === compare)?.short;

	const openWith = (next: boolean) => {
		if (next) setDraft({ range, compare });
		setOpen(next);
	};

	const apply = () => {
		onApply(draft.range, draft.compare);
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={openWith}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-800 hover:bg-gray-50"
					title="Period och jämförelse"
				>
					<CalendarDays className="h-4 w-4 text-gray-500" />
					<span className="font-medium">
						{range.preset === "custom" ? "Eget intervall" : presetLabel(range.preset)}
					</span>
					<span className="text-gray-500">{formatRangeShort(resolved)}</span>
					{compareLabel && (
						<span className="rounded bg-action/10 px-1.5 py-0.5 text-xs font-medium text-action">
							jmf. {compareLabel}
						</span>
					)}
					<ChevronDown className="h-4 w-4 text-gray-400" />
				</button>
			</PopoverTrigger>
			<PopoverContent align="end" className="z-[1300] w-[540px] p-0">
				<div className="grid grid-cols-[200px_1fr]">
					<ul className="border-r border-gray-200 py-2">
						{DATE_RANGE_PRESETS.filter((p) => p.value !== "custom").map((p) => (
							<li key={p.value}>
								<button
									type="button"
									onClick={() =>
										setDraft((d) => ({ ...d, range: { preset: p.value as DateRangePreset } }))
									}
									className={cn(
										"w-full px-4 py-1.5 text-left text-sm hover:bg-gray-100",
										draft.range.preset === p.value
											? "bg-action/10 font-medium text-action"
											: "text-gray-800"
									)}
								>
									{p.label}
								</button>
							</li>
						))}
					</ul>
					<div className="flex flex-col gap-4 p-4">
						<div>
							<p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
								Period
							</p>
							<div className="flex items-center gap-2">
								<input
									type="date"
									value={draftResolved.from}
									max={draftResolved.to}
									onChange={(e) =>
										setDraft((d) => ({
											...d,
											range: { preset: "custom", from: e.target.value, to: draftResolved.to },
										}))
									}
									className="h-9 rounded-md border border-gray-300 px-2 text-sm"
								/>
								<span className="text-gray-400">–</span>
								<input
									type="date"
									value={draftResolved.to}
									min={draftResolved.from}
									onChange={(e) =>
										setDraft((d) => ({
											...d,
											range: { preset: "custom", from: draftResolved.from, to: e.target.value },
										}))
									}
									className="h-9 rounded-md border border-gray-300 px-2 text-sm"
								/>
							</div>
						</div>
						<div>
							<label className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
								<Checkbox
									checked={!!draft.compare}
									onCheckedChange={(checked) =>
										setDraft((d) => ({ ...d, compare: checked ? "previous_period" : undefined }))
									}
								/>
								Jämför med
							</label>
							{draft.compare && (
								<Select
									value={draft.compare}
									onValueChange={(v) => setDraft((d) => ({ ...d, compare: v as ComparisonMode }))}
								>
									<SelectTrigger className="mt-2 w-full">
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="z-[1400]">
										{COMPARE_MODES.map((o) => (
											<SelectItem key={o.value} value={o.value}>
												{o.label.replace(/^Mot /, "")}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						</div>
						<div className="mt-auto flex justify-end gap-2 border-t border-gray-200 pt-3">
							<button
								type="button"
								onClick={() => setOpen(false)}
								className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
							>
								Avbryt
							</button>
							<button
								type="button"
								onClick={apply}
								className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white"
							>
								Tillämpa
							</button>
						</div>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default DateRangePicker;
